import * as React from 'react';
import { useCallback, useState } from 'react';
import type { DragEvent } from 'react';

interface UseDropzoneParams {
  onFilesSelected: (files: Array<File>) => void;
  isDisabled?: boolean;
  acceptedFileTypes: Array<string>;
  maxFileCount: number;
  currentFilesCount: number;
}

export function useDropzone({
  onFilesSelected,
  isDisabled = false,
  acceptedFileTypes,
  maxFileCount,
  currentFilesCount,
}: UseDropzoneParams) {
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (isDisabled) return;
      // For multiple files mode, block if at capacity
      // For single file mode, always allow (will replace existing file)
      if (maxFileCount > 1 && currentFilesCount >= maxFileCount) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onFilesSelected(maxFileCount > 1 ? files : [files[0]]);
      }
    },
    [isDisabled, maxFileCount, currentFilesCount, onFilesSelected]
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (isDisabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(true);
    },
    [isDisabled]
  );

  const handleDragLeave = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (isDisabled) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
    },
    [isDisabled]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isDisabled) return;
      // For multiple files mode, block if at capacity
      // For single file mode, always allow (will replace existing file)
      if (maxFileCount > 1 && currentFilesCount >= maxFileCount) return;
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        onFilesSelected(files);
      }
      e.target.value = '';
    },
    [isDisabled, maxFileCount, currentFilesCount, onFilesSelected]
  );

  return {
    isDragActive,
    getRootProps: () => ({
      onDrop: handleDrop,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
    }),
    getInputProps: () => ({
      type: 'file' as const,
      accept: acceptedFileTypes.join(','),
      onChange: handleFileInput,
      multiple: maxFileCount > 1 ? true : false,
      style: { display: 'none' },
    }),
  };
}
